import { useState, useEffect } from 'react';
import '@/App.css';
import axios from 'axios';
import { Scale, FileText, Gavel, Download, Loader2, Send, MessageSquare, BookOpen, Users, Cog, HelpCircle, MapPin, Building2, Bot, X, ChevronDown, LogIn, History, Mail, Phone, Shield, RotateCcw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { AuthModal, UserMenu, useAuth } from '@/components/Auth';
import { WordCounter, PricingTable, PaymentButton } from '@/components/Pricing';
import { ContactPage, AboutPage, TermsPage, PrivacyPage, FAQPage, RefundPage } from '@/components/LegalPages';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  // Auth state
  const { user, isLoading: authLoading, login, logout } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [caseHistory, setCaseHistory] = useState([]);
  
  // Legal pages state
  const [currentPage, setCurrentPage] = useState(null); // 'contact', 'about', 'terms', 'privacy', 'faq', 'refund'
  
  // Form state
  const [judgeName, setJudgeName] = useState('');
  const [caseNumber, setCaseNumber] = useState('');
  const [documentText, setDocumentText] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [verdict, setVerdict] = useState(null);
  const [reasoning, setReasoning] = useState(null);
  const [currentCaseId, setCurrentCaseId] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [observations, setObservations] = useState('');
  const [isSendingToClerk, setIsSendingToClerk] = useState(false);
  const [sentToClerk, setSentToClerk] = useState(false);
  const [includeRoJurisprudence, setIncludeRoJurisprudence] = useState(false);
  const [includeEuJurisprudence, setIncludeEuJurisprudence] = useState(false);
  const [showChatbot, setShowChatbot] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [isSendingChat, setIsSendingChat] = useState(false);

  // Fetch case history when user logs in
  useEffect(() => {
    if (user) {
      fetchCaseHistory();
    }
  }, [user]);

  // Auto-analyze after returning from Stripe payment
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const pendingCaseId = localStorage.getItem('pending_payment_case_id');
    
    if (sessionId && pendingCaseId && user) {
      // Returning from Stripe payment — auto-analyze the pending case
      const autoAnalyze = async () => {
        setIsAnalyzing(true);
        try {
          // Load the case data first
          const caseResponse = await axios.get(`${API}/cases/${pendingCaseId}`);
          const caseData = caseResponse.data;
          
          // Populate the form with case data
          setJudgeName(caseData.judge_name);
          setCaseNumber(caseData.case_number);
          setDocumentText(caseData.document_text);
          setCurrentCaseId(pendingCaseId);
          
          // Trigger analysis
          const analysisResponse = await axios.post(`${API}/cases/${pendingCaseId}/analyze`);
          setVerdict(analysisResponse.data.verdict);
          setReasoning(analysisResponse.data.reasoning);
          setShowResults(true);
          toast.success('Plată confirmată! Analiză completă.');
        } catch (error) {
          console.error('Auto-analyze error:', error);
          toast.error('Eroare la analiza automată. Încercați din nou.');
        } finally {
          setIsAnalyzing(false);
          localStorage.removeItem('pending_payment_case_id');
          // Clean URL
          window.history.replaceState(null, '', window.location.pathname);
        }
      };
      autoAnalyze();
    }
  }, [user]);

  const fetchCaseHistory = async () => {
    try {
      const response = await axios.get(`${API}/cases/history`);
      setCaseHistory(response.data.cases || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const loadCaseFromHistory = (historyCase) => {
    setJudgeName(historyCase.judge_name);
    setCaseNumber(historyCase.case_number);
    setDocumentText(historyCase.document_text);
    if (historyCase.analyzed && historyCase.verdict) {
      setVerdict(historyCase.verdict);
      setReasoning(historyCase.reasoning);
      setCurrentCaseId(historyCase.id);
      setShowResults(true);
    }
    setShowHistory(false);
    toast.success('Dosar încărcat din istoric');
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    console.log('Upload starting:', file.name, file.size, file.type);
    setIsUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      console.log('Sending to:', `${API}/upload-document`);
      const response = await axios.post(`${API}/upload-document`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 120000
      });
      
      console.log('Upload response:', response.data);
      setDocumentText(response.data.text);
      setUploadedFileName(response.data.filename);
      toast.success(`Document încărcat: ${response.data.filename}`);
    } catch (error) {
      console.error('Upload error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Eroare necunoscută';
      toast.error(`Eroare încărcare: ${errorMsg}`);
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!judgeName || !caseNumber || !documentText) {
      toast.error('Completați toate câmpurile');
      return;
    }

    setIsAnalyzing(true);
    setShowResults(false);

    try {
      const caseResponse = await axios.post(`${API}/cases`, {
        judge_name: judgeName,
        case_number: caseNumber,
        document_text: documentText,
        include_ro_jurisprudence: includeRoJurisprudence,
        include_eu_jurisprudence: includeEuJurisprudence
      });

      const caseId = caseResponse.data.id;
      setCurrentCaseId(caseId);

      const analysisResponse = await axios.post(`${API}/cases/${caseId}/analyze`);
      
      setVerdict(analysisResponse.data.verdict);
      setReasoning(analysisResponse.data.reasoning);
      setShowResults(true);
      toast.success('Analiză completă!');
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error('Analiza gratuită a fost utilizată. Efectuați plata pentru a continua.');
      } else {
        toast.error('Eroare la analiză');
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleExportPDF = async () => {
    if (!currentCaseId) return;

    try {
      const response = await axios.get(`${API}/cases/${currentCaseId}/export-pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sentinta_${caseNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF descărcat!');
    } catch (error) {
      toast.error('Eroare PDF');
    }
  };

  const handleSendToClerk = async () => {
    if (!observations.trim()) {
      toast.error('Adăugați observații');
      return;
    }

    setIsSendingToClerk(true);

    try {
      await axios.post(`${API}/cases/${currentCaseId}/observations`, {
        observations: observations
      });

      await axios.post(`${API}/cases/${currentCaseId}/send-to-clerk`);
      
      setSentToClerk(true);
      toast.success('Trimis la grefier!');
    } catch (error) {
      toast.error('Eroare');
    } finally {
      setIsSendingToClerk(false);
    }
  };

  const handleReset = () => {
    setJudgeName('');
    setCaseNumber('');
    setDocumentText('');
    setUploadedFileName('');
    setVerdict(null);
    setReasoning(null);
    setCurrentCaseId(null);
    setShowResults(false);
    setObservations('');
    setSentToClerk(false);
    setIncludeRoJurisprudence(false);
    setIncludeEuJurisprudence(false);
  };

  const handleSendChatMessage = async () => {
    if (!chatMessage.trim()) return;
    
    const newMessage = {
      id: Date.now(),
      text: chatMessage,
      sender: 'user',
      timestamp: new Date().toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })
    };
    
    setChatMessages(prev => [...prev, newMessage]);
    setChatMessage('');
    setIsSendingChat(true);

    try {
      await axios.post(`${API}/chatbot`, { message: chatMessage });
      
      const botResponse = {
        id: Date.now() + 1,
        text: 'Vă mulțumim pentru mesaj! Un reprezentant vă va contacta în cel mai scurt timp posibil.',
        sender: 'bot',
        timestamp: new Date().toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })
      };
      setChatMessages(prev => [...prev, botResponse]);
    } catch (error) {
      const botResponse = {
        id: Date.now() + 1,
        text: 'Mesajul dvs. a fost înregistrat. Vă vom contacta în curând.',
        sender: 'bot',
        timestamp: new Date().toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })
      };
      setChatMessages(prev => [...prev, botResponse]);
    } finally {
      setIsSendingChat(false);
    }
  };

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  // Render legal pages if one is selected
  if (currentPage === 'contact') return <ContactPage onBack={() => setCurrentPage(null)} />;
  if (currentPage === 'about') return <AboutPage onBack={() => setCurrentPage(null)} />;
  if (currentPage === 'terms') return <TermsPage onBack={() => setCurrentPage(null)} />;
  if (currentPage === 'privacy') return <PrivacyPage onBack={() => setCurrentPage(null)} />;
  if (currentPage === 'faq') return <FAQPage onBack={() => setCurrentPage(null)} />;
  if (currentPage === 'refund') return <RefundPage onBack={() => setCurrentPage(null)} />;

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <div className="grain-overlay" />
      
      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)}
        onAuthSuccess={login}
      />

      {/* Fixed Auth Header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-primary/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => setCurrentPage(null)}>
            <Scale className="w-6 h-6 text-secondary" />
            <span className="font-serif font-bold text-white hidden sm:block">Judecătorul Digital</span>
          </div>
          
          <div className="flex items-center gap-3">
            {user && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowHistory(true)}
                className="text-white/80 hover:text-white hover:bg-white/10"
              >
                <History className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Istoric</span>
              </Button>
            )}
            
            {authLoading ? (
              <Loader2 className="w-5 h-5 text-white animate-spin" />
            ) : user ? (
              <UserMenu user={user} onLogout={logout} />
            ) : (
              <Button
                onClick={() => setShowAuthModal(true)}
                size="sm"
                className="bg-secondary text-primary hover:bg-secondary/90"
              >
                <LogIn className="w-4 h-4 mr-1" />
                Autentificare
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* History Sidebar */}
      <AnimatePresence>
        {showHistory && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50"
            onClick={() => setShowHistory(false)}
          >
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-card shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-border flex items-center justify-between bg-primary text-white">
                <h3 className="font-serif text-xl font-bold flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Istoric Dosare
                </h3>
                <button onClick={() => setShowHistory(false)} className="text-white/70 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-4 overflow-y-auto h-[calc(100vh-60px)]">
                {caseHistory.length === 0 ? (
                  <div className="text-center text-muted-foreground py-8">
                    <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Nu aveți dosare analizate încă.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {caseHistory.map((historyCase) => (
                      <button
                        key={historyCase.id}
                        onClick={() => loadCaseFromHistory(historyCase)}
                        className="w-full text-left p-4 bg-muted/30 hover:bg-muted/50 rounded-lg border border-border transition-colors"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-mono text-sm text-primary font-semibold">
                            {historyCase.case_number}
                          </span>
                          {historyCase.analyzed && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                              Analizat
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Judecător: {historyCase.judge_name}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(historyCase.created_at).toLocaleDateString('ro-RO')}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero Section */}
      <div 
        className="relative h-[50vh] flex items-center justify-center overflow-hidden pt-12"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1589578527966-fdac0f44566c?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-primary/80 via-primary/70 to-background" />
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center px-4"
        >
          <Scale className="w-16 h-16 mx-auto mb-4 text-secondary" />
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
            Judecătorul Digital
          </h1>
          <p className="text-lg text-white/90 font-sans mb-6">Justiție modernă prin inteligență artificială</p>
          <Button 
            onClick={() => scrollToSection('app-section')}
            className="bg-secondary text-primary hover:bg-secondary/90"
          >
            <ChevronDown className="mr-2 h-4 w-4" />
            Începe Analiza
          </Button>
        </motion.div>
      </div>

      {/* About Section */}
      <section id="about-section" className="py-16 bg-card/50">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-12">
              <Scale className="w-12 h-12 mx-auto mb-4 text-secondary" />
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-primary mb-4">Despre Judecătorul Digital</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-card p-6 rounded-lg border-2 border-primary/10 shadow-lg">
                <p className="text-foreground leading-relaxed mb-4">
                  <strong className="text-primary">JUDECĂTORUL DIGITAL</strong> este o arhitectură neuronală de inteligențe artificiale, 
                  constituită sub forma performantă, de ultimă oră, a unei <strong>Inteligențe Sintetice</strong>, care constituie o resursă 
                  care vine în sprijinul celor care se documentează pentru a verifica dacă soluțiile date de instanțe, soluții propuse 
                  de avocați și juriști pentru persoane fizice și juridice sunt conform legilor românești, cu verificarea, în premieră, 
                  a <strong>jurisprudenței românești și europene</strong>.
                </p>
                <p className="text-foreground leading-relaxed">
                  JUDECĂTORUL DIGITAL asigură acces rapid și fiabil la informații juridice, fiind un instrument digital de ultimă 
                  generație care asigură unul din drepturile fundamentale ale cetățenilor europeni prevăzute expres în 
                  <strong className="text-secondary"> CARTA DREPTURILOR FUNDAMENTALE A UNIUNII EUROPENE</strong>, accesul românilor la o judecată corectă și transparentă.
                </p>
              </div>
              
              <div className="bg-card p-6 rounded-lg border-2 border-secondary/20 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <Users className="w-8 h-8 text-secondary" />
                  <h3 className="font-serif text-xl font-bold text-primary">Cine sunt constructorii</h3>
                </div>
                <p className="text-foreground leading-relaxed">
                  JUDECĂTORUL DIGITAL este o aplicație realizată de noul val de tineri specialiști în IA, dublat de experți în drept 
                  și științe juridice, care au beneficiat de motorul militar de optimizare <strong className="text-secondary">S8NTH STRATEGIC ENGINE</strong>!
                </p>
                <div className="mt-6 p-4 bg-primary/5 rounded border-l-4 border-secondary">
                  <p className="text-sm text-muted-foreground italic">
                    "Calitatea răspunsurilor date de JUDECĂTORUL DIGITAL este dată și de faptul că inteligența artificială folosește, 
                    în medie, <strong>50 rezultate de căutare</strong> (în comparație cu 1-4 rezultate folosite de ChatGPT) și utilizează 
                    interogarea multiplă, online, pentru un răspuns consolidat."
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How it Works Section */}
      <section id="how-it-works" className="py-16 bg-background">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-12">
              <Cog className="w-12 h-12 mx-auto mb-4 text-secondary" />
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-primary mb-4">Cum funcționează</h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-card p-6 rounded-lg border-2 border-primary/10 text-center">
                <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-secondary">1</span>
                </div>
                <h3 className="font-semibold text-primary mb-2">Acces Rapid</h3>
                <p className="text-sm text-muted-foreground">
                  JUDECĂTORUL DIGITAL asigură accesul rapid și fiabil la informații juridice, fiind un instrument digital de ultimă generație 
                  cu o structură bazată pe interogări multiple ale Inteligențelor Artificiale.
                </p>
              </div>
              
              <div className="bg-card p-6 rounded-lg border-2 border-primary/10 text-center">
                <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-secondary">2</span>
                </div>
                <h3 className="font-semibold text-primary mb-2">Rezultate Precise</h3>
                <p className="text-sm text-muted-foreground">
                  Este o modalitate prin care cetățenii și profesioniștii pot afla ce se află în spatele unei Hotărâri Judecătorești, 
                  cu aberații spre zero.
                </p>
              </div>
              
              <div className="bg-card p-6 rounded-lg border-2 border-primary/10 text-center">
                <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-secondary">3</span>
                </div>
                <h3 className="font-semibold text-primary mb-2">Verificare Corectă</h3>
                <p className="text-sm text-muted-foreground">
                  <strong>JUDECĂTORUL DIGITAL</strong> este singurul instrument care îți poate spune dacă ai fost, sau vei fi judecat, corect!
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How to Use Section */}
      <section id="how-to-use" className="py-16 bg-card/50">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-12">
              <HelpCircle className="w-12 h-12 mx-auto mb-4 text-secondary" />
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-primary mb-4">Cum se utilizează</h2>
            </div>
            
            <div className="bg-card p-8 rounded-lg border-2 border-primary/10 shadow-lg">
              <div className="space-y-4">
                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 bg-secondary text-primary rounded-full flex items-center justify-center flex-shrink-0 font-bold">1</div>
                  <p className="text-foreground">Te autentifici cu datele personale pe pagină. Aceste date îți vor folosi la fiecare logare pentru ca Platforma să te identifice (în vederea aplicării discounturilor sau accesării istoricului).</p>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 bg-secondary text-primary rounded-full flex items-center justify-center flex-shrink-0 font-bold">2</div>
                  <p className="text-foreground">După autentificare programul te introduce pe pagina JUDECĂTORULUI DIGITAL.</p>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 bg-secondary text-primary rounded-full flex items-center justify-center flex-shrink-0 font-bold">3</div>
                  <p className="text-foreground">Începe să completezi spațiile libere (dacă nu știi numele judecătorului sau faci numai o verificare înainte de proces, poți introduce orice nume).</p>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 bg-secondary text-primary rounded-full flex items-center justify-center flex-shrink-0 font-bold">4</div>
                  <p className="text-foreground">La numărul dosarului, dacă nu îl știi sau nu îl ai încă, poți introduce orice număr. Programul este pregătit pentru judecători în funcție.</p>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 bg-secondary text-primary rounded-full flex items-center justify-center flex-shrink-0 font-bold">5</div>
                  <p className="text-foreground">Urmează două ferestre pentru introducerea documentelor, dacă există, sub formă de DOCX, PDF.</p>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 bg-secondary text-primary rounded-full flex items-center justify-center flex-shrink-0 font-bold">6</div>
                  <p className="text-foreground">Urmează o fereastră în care poți introduce text pentru a explica mai bine JUDECĂTORULUI DIGITAL despre ce este vorba.</p>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 bg-secondary text-primary rounded-full flex items-center justify-center flex-shrink-0 font-bold">7</div>
                  <p className="text-foreground">Apoi poți activa cele două butoane de <strong>JURISPRUDENȚĂ NAȚIONALĂ</strong> și/sau <strong>JURISPRUDENȚĂ EUROPEANĂ</strong>. Sfatul nostru este ca cele două butoane să fie activate doar la spețe de importanță mare.</p>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 bg-secondary text-primary rounded-full flex items-center justify-center flex-shrink-0 font-bold">8</div>
                  <p className="text-foreground">După ce ați urmat toți pașii de mai sus puteți apăsa <strong>JUDECĂ</strong>. Programul va afișa într-un timp foarte scurt <strong>SENTINȚA</strong> și <strong>MOTIVAREA</strong>.</p>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 bg-secondary text-primary rounded-full flex items-center justify-center flex-shrink-0 font-bold">9</div>
                  <p className="text-foreground">Butonul pentru COMENTARII este pentru judecători care pot solicita JUDECĂTORULUI DIGITAL, prin Grefier, date suplimentare pentru desfășurarea procesului.</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Application Section */}
      <section id="app-section" className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4">
          <AnimatePresence mode="wait">
            {!showResults ? (
              <motion.div
                key="form"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
              >
                <div className="max-w-4xl mx-auto">
                  <div className="bg-card border-2 border-primary/10 p-8 md:p-12 shadow-xl relative">
                    <div className="absolute top-0 right-0 w-16 h-16 border-t-2 border-r-2 border-secondary" />
                    <div className="absolute bottom-0 left-0 w-16 h-16 border-b-2 border-l-2 border-secondary" />

                    <div className="flex items-center gap-3 mb-8">
                      <FileText className="w-8 h-8 text-secondary" />
                      <h2 className="font-serif text-3xl font-bold text-primary">Dosarul Cauzei</h2>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-8">
                      <div className="space-y-2">
                        <Label className="uppercase tracking-widest text-xs font-bold text-muted-foreground">
                          Numele și Prenumele Judecătorului
                        </Label>
                        <input
                          data-testid="judge-name-input"
                          type="text"
                          value={judgeName}
                          onChange={(e) => setJudgeName(e.target.value)}
                          className="w-full bg-transparent border-b-2 border-border focus:border-primary outline-none px-0 py-4 text-lg font-sans transition-colors"
                          placeholder="Ex: Ion Popescu"
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="uppercase tracking-widest text-xs font-bold text-muted-foreground">
                          Număr Dosar
                        </Label>
                        <input
                          data-testid="case-number-input"
                          type="text"
                          value={caseNumber}
                          onChange={(e) => setCaseNumber(e.target.value)}
                          className="w-full bg-transparent border-b-2 border-border focus:border-primary outline-none px-0 py-4 text-lg font-mono transition-colors"
                          placeholder="Ex: 12345/2025"
                          required
                        />
                      </div>

                      <div className="space-y-4">
                        <Label className="uppercase tracking-widest text-xs font-bold text-muted-foreground">
                          Documente Dosar
                        </Label>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className={`border-2 ${isUploading ? 'border-secondary bg-secondary/10' : 'border-primary/30 hover:border-primary'} p-6 text-center transition-colors rounded`}>
                            <input 
                              type="file" 
                              id="pdf-upload" 
                              data-testid="pdf-upload-input"
                              accept=".pdf" 
                              onChange={handleFileUpload} 
                              className="hidden" 
                              disabled={isUploading}
                            />
                            <label htmlFor="pdf-upload" className={`cursor-pointer ${isUploading ? 'pointer-events-none' : ''}`}>
                              {isUploading ? (
                                <Loader2 className="w-12 h-12 mx-auto mb-3 text-secondary animate-spin" />
                              ) : (
                                <FileText className="w-12 h-12 mx-auto mb-3 text-primary" />
                              )}
                              <p className="text-lg font-semibold text-primary mb-1">PDF</p>
                              <p className="text-xs text-muted-foreground">
                                {isUploading ? 'Se încarcă...' : 'Încarcă fișier PDF'}
                              </p>
                            </label>
                          </div>
                          
                          <div className={`border-2 ${isUploading ? 'border-secondary bg-secondary/10' : 'border-primary/30 hover:border-primary'} p-6 text-center transition-colors rounded`}>
                            <input 
                              type="file" 
                              id="docx-upload" 
                              data-testid="docx-upload-input"
                              accept=".docx,.doc" 
                              onChange={handleFileUpload} 
                              className="hidden" 
                              disabled={isUploading}
                            />
                            <label htmlFor="docx-upload" className={`cursor-pointer ${isUploading ? 'pointer-events-none' : ''}`}>
                              {isUploading ? (
                                <Loader2 className="w-12 h-12 mx-auto mb-3 text-secondary animate-spin" />
                              ) : (
                                <FileText className="w-12 h-12 mx-auto mb-3 text-primary" />
                              )}
                              <p className="text-lg font-semibold text-primary mb-1">DOCX</p>
                              <p className="text-xs text-muted-foreground">
                                {isUploading ? 'Se încarcă...' : 'Încarcă fișier DOCX'}
                              </p>
                            </label>
                          </div>
                        </div>
                        
                        {uploadedFileName && (
                          <div className="p-3 bg-secondary/10 border-l-4 border-secondary">
                            <p className="text-sm font-medium text-secondary">✓ {uploadedFileName}</p>
                          </div>
                        )}

                        <div className="text-center text-sm text-muted-foreground">SAU</div>

                        <Textarea
                          value={documentText}
                          onChange={(e) => setDocumentText(e.target.value)}
                          placeholder="Tastați textul aici..."
                          className="min-h-[200px] font-sans text-base border-2 focus:border-primary"
                          required
                        />
                        
                        {/* Word Counter and Pricing */}
                        {documentText && (
                          <WordCounter text={documentText} />
                        )}
                      </div>

                      <div className="space-y-4 pt-6 border-t-2 border-border">
                        <div className="flex items-center gap-3 mb-4">
                          <BookOpen className="w-6 h-6 text-secondary" />
                          <Label className="uppercase tracking-widest text-xs font-bold text-muted-foreground">
                            Include Jurisprudență
                          </Label>
                        </div>

                        <div className="flex items-center justify-between p-4 bg-muted/30">
                          <label>Jurisprudență România</label>
                          <Switch
                            checked={includeRoJurisprudence}
                            onCheckedChange={setIncludeRoJurisprudence}
                          />
                        </div>

                        <div className="flex items-center justify-between p-4 bg-muted/30">
                          <label>Jurisprudență UE</label>
                          <Switch
                            checked={includeEuJurisprudence}
                            onCheckedChange={setIncludeEuJurisprudence}
                          />
                        </div>
                      </div>

                      {/* Payment Section */}
                      {documentText && (
                        <div className="pt-6 border-t-2 border-border">
                          <PaymentButton 
                            wordCount={documentText.split(/\s+/).filter(w => w.length > 0).length}
                            userId={user?.user_id}
                            caseId={currentCaseId}
                            disabled={isAnalyzing || isUploading || !documentText}
                          />
                        </div>
                      )}

                      <div className="flex flex-col gap-4 pt-6">
                        <p className="text-center text-xs text-muted-foreground">
                          Pentru testare, puteți folosi butonul de mai jos fără plată:
                        </p>
                        <Button
                          type="submit"
                          data-testid="analyze-case-btn"
                          disabled={isAnalyzing || isUploading}
                          className="w-full bg-primary/70 text-primary-foreground h-12"
                        >
                          {isAnalyzing ? (
                            <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Se analizează...</>
                          ) : (
                            <><Gavel className="mr-2 h-5 w-5" />Judecă Cazul (Test gratuit)</>
                          )}
                        </Button>
                        {isAnalyzing && (
                          <p className="text-center text-sm text-muted-foreground">
                            Analiza poate dura între 30-90 de secunde. Vă rugăm așteptați...
                          </p>
                        )}
                      </div>
                    </form>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div key="results" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="max-w-6xl mx-auto">
                  <div className="bg-card border-2 border-secondary/20 p-8 shadow-2xl">
                    <div className="flex items-center justify-between mb-8">
                      <div className="flex items-center gap-3">
                        <Gavel className="w-8 h-8 text-secondary" />
                        <h2 className="font-serif text-3xl font-bold text-primary">Sentința</h2>
                      </div>
                      <div className="flex gap-3">
                        <Button onClick={handleExportPDF} variant="outline"><Download className="mr-2 h-4 w-4" />Export PDF</Button>
                        <Button onClick={handleReset} variant="outline">Caz Nou</Button>
                      </div>
                    </div>

                    <div className="mb-8 p-6 bg-muted/30 border-l-4 border-secondary">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 font-mono text-sm">
                        <div><span className="text-muted-foreground">Dosar:</span> <span className="ml-2 font-semibold">{caseNumber}</span></div>
                        <div><span className="text-muted-foreground">Judecător:</span> <span className="ml-2 font-semibold">{judgeName}</span></div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      <div className="space-y-4">
                        <h3 className="font-serif text-xl font-bold text-primary border-b-2 border-secondary pb-2">DISPOZITIV</h3>
                        <p className="text-foreground whitespace-pre-wrap">{verdict}</p>
                      </div>
                      <div className="space-y-4">
                        <h3 className="font-serif text-xl font-bold text-primary border-b-2 border-secondary pb-2">MOTIVARE</h3>
                        <div className="max-h-[500px] overflow-y-auto"><p className="text-foreground whitespace-pre-wrap">{reasoning}</p></div>
                      </div>
                    </div>

                    <div className="mt-8 pt-8 border-t-2 border-primary/10">
                      <div className="flex items-center gap-3 mb-4">
                        <MessageSquare className="w-6 h-6 text-secondary" />
                        <h3 className="font-serif text-2xl font-bold text-primary">Observații Judecător</h3>
                      </div>
                      <Textarea value={observations} onChange={(e) => setObservations(e.target.value)} placeholder="Observații..." className="min-h-[120px]" disabled={sentToClerk} />
                      {sentToClerk && <div className="mt-4 p-4 bg-secondary/10"><p className="text-sm font-medium text-secondary">✓ Trimis la grefier</p></div>}
                      {observations.trim() && !sentToClerk && (
                        <Button onClick={handleSendToClerk} disabled={isSendingToClerk} className="mt-4 bg-secondary text-primary">
                          {isSendingToClerk ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Se trimite...</> : <><Send className="mr-2 h-5 w-5" />Trimite Grefier</>}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact-section" className="py-16 bg-primary text-white">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-8">
              <Building2 className="w-12 h-12 mx-auto mb-4 text-secondary" />
              <h2 className="font-serif text-3xl sm:text-4xl font-bold mb-2">Contact</h2>
              <p className="text-white/70">ALIANȚA PENTRU NATURA ONG ROMANIA</p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg text-center">
                <MapPin className="w-8 h-8 mx-auto mb-3 text-secondary" />
                <h3 className="font-semibold mb-2">Adresă</h3>
                <p className="text-sm text-white/80">Calea Smârdan nr. 2</p>
                <p className="text-sm text-white/80">Parc Industrial ATLAS, Et. 2</p>
                <p className="text-sm text-white/80">Galați, România</p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg text-center">
                <Mail className="w-8 h-8 mx-auto mb-3 text-secondary" />
                <h3 className="font-semibold mb-2">E-mail</h3>
                <a href="mailto:ncsllcia@gmail.com" className="text-secondary hover:underline">
                  ncsllcia@gmail.com
                </a>
                <p className="text-sm text-white/60 mt-2">Răspuns în 24h</p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg text-center">
                <Phone className="w-8 h-8 mx-auto mb-3 text-secondary" />
                <h3 className="font-semibold mb-2">Program</h3>
                <p className="text-sm text-white/80">Luni - Duminică</p>
                <p className="text-lg font-bold text-secondary">24/7</p>
              </div>
            </div>
            
            <div className="text-center mt-8">
              <Button
                onClick={() => setCurrentPage('contact')}
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10"
              >
                Mai multe detalii
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-primary/95 text-white/70 border-t border-white/10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Company */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Scale className="w-5 h-5 text-secondary" />
                <span className="font-serif font-bold text-white">Judecătorul Digital</span>
              </div>
              <p className="text-sm">ALIANȚA PENTRU NATURA ONG ROMANIA</p>
              <p className="text-xs mt-2">Justiție modernă prin inteligență artificială</p>
            </div>
            
            {/* Legal */}
            <div>
              <h4 className="font-semibold text-white mb-3">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => setCurrentPage('terms')} className="hover:text-secondary transition-colors">Termeni și Condiții</button></li>
                <li><button onClick={() => setCurrentPage('privacy')} className="hover:text-secondary transition-colors">Politica de Confidențialitate</button></li>
                <li><button onClick={() => setCurrentPage('refund')} className="hover:text-secondary transition-colors">Politica de Returnare</button></li>
              </ul>
            </div>
            
            {/* Info */}
            <div>
              <h4 className="font-semibold text-white mb-3">Informații</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => setCurrentPage('about')} className="hover:text-secondary transition-colors">Despre Noi</button></li>
                <li><button onClick={() => setCurrentPage('faq')} className="hover:text-secondary transition-colors">Întrebări Frecvente</button></li>
                <li><button onClick={() => setCurrentPage('contact')} className="hover:text-secondary transition-colors">Contact</button></li>
              </ul>
            </div>
            
            {/* Contact */}
            <div>
              <h4 className="font-semibold text-white mb-3">Contact</h4>
              <p className="text-sm flex items-center gap-2">
                <Mail className="w-4 h-4" />
                ncsllcia@gmail.com
              </p>
              <p className="text-sm mt-2">Galați, România</p>
              <p className="text-xs mt-2">Suport 24/7 prin botchat</p>
            </div>
          </div>
          
          <div className="border-t border-white/10 pt-6 text-center text-xs">
            <p>© 2024 ALIANȚA PENTRU NATURA ONG ROMANIA. Toate drepturile rezervate.</p>
            <p className="mt-1">Jurisdicție: România | Instanța competentă: Galați</p>
          </div>
        </div>
      </footer>

      {/* Chatbot Button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1, type: 'spring' }}
        onClick={() => setShowChatbot(!showChatbot)}
        className="fixed bottom-24 right-6 w-16 h-16 bg-secondary text-primary rounded-full shadow-2xl flex items-center justify-center hover:bg-secondary/90 transition-all z-50"
        data-testid="chatbot-toggle"
      >
        {showChatbot ? <X className="w-7 h-7" /> : <Bot className="w-7 h-7" />}
      </motion.button>

      {/* Chatbot Window */}
      <AnimatePresence>
        {showChatbot && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            className="fixed bottom-44 right-6 w-80 sm:w-96 bg-card border-2 border-primary/20 rounded-lg shadow-2xl overflow-hidden z-50"
          >
            <div className="bg-primary text-white p-4 flex items-center gap-3">
              <Bot className="w-6 h-6" />
              <div>
                <h3 className="font-semibold">Asistent Virtual</h3>
                <p className="text-xs text-white/70">Suntem aici să vă ajutăm</p>
              </div>
            </div>
            
            <div className="h-72 overflow-y-auto p-4 space-y-3 bg-muted/30">
              {chatMessages.length === 0 && (
                <div className="text-center text-muted-foreground text-sm py-8">
                  <Bot className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Bună ziua! Cum vă putem ajuta?</p>
                </div>
              )}
              {chatMessages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] p-3 rounded-lg ${
                      msg.sender === 'user'
                        ? 'bg-primary text-white rounded-br-none'
                        : 'bg-card border border-border rounded-bl-none'
                    }`}
                  >
                    <p className="text-sm">{msg.text}</p>
                    <p className={`text-xs mt-1 ${msg.sender === 'user' ? 'text-white/60' : 'text-muted-foreground'}`}>
                      {msg.timestamp}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="p-3 border-t border-border bg-card">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendChatMessage()}
                  placeholder="Scrieți mesajul..."
                  className="flex-1 px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:border-primary"
                  disabled={isSendingChat}
                />
                <Button
                  onClick={handleSendChatMessage}
                  disabled={isSendingChat || !chatMessage.trim()}
                  size="sm"
                  className="bg-secondary text-primary hover:bg-secondary/90"
                >
                  {isSendingChat ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;
